import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { MetaScopeOptions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import type { Tag } from '@core/tags-and-sources-types';

import { EntityStoreKey } from '../../types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Tag>()(
  EntityStoreKey.Tags,
  {
    customActions: adapter => ({
      createTag: (
        state,
        payload: {
          candidateId?: number;
          tagName: string | string[];
        },
      ) => adapter.markAsPending(state),
      createTagSuccess: (state, payload: Tag[]) =>
        pipeState(adapter.addMany(state, payload), next => adapter.markAsLoaded(next)),
      deleteCandidateTags: (
        state,
        payload: {
          candidateId: number;
          tagName: string | string[];
        },
      ) => adapter.markAsPending(state),
      deleteCandidateTagsSuccess: (state, payload: Tag[]) =>
        pipeState(
          adapter.removeMany(
            state,
            payload.map(tag => tag.id),
          ),
          next => adapter.markAsLoaded(next),
        ),
      deleteTags: (state, payload: Array<Tag['id']>) => adapter.markAsPending(state),
      deleteTagsSuccess: (state, payload: number[]) =>
        pipeState(adapter.removeMany(state, payload), next => adapter.markAsLoaded(next)),
      updateTag: (
        state,
        payload: {
          tagId: number;
          tagName: string;
        },
      ) => adapter.markAsPending(state),
      updateTagSuccess: (state, payload: Tag) =>
        pipeState(adapter.updateOne(state, payload), next => adapter.markAsLoaded(next)),
      updateTagFailed: ngrxEvent<HttpErrorResponse>(),
      mergeTags: (
        state,
        payload: {
          tagIds: number[];
          tagName: string;
        },
      ) => adapter.markAsPending(state),
      mergeTagsSuccess: (state, payload: { tag: Tag; tagIds: number[] }) =>
        pipeState(
          adapter.removeMany(state, payload.tagIds),
          next => adapter.addOne(next, payload.tag),
          next => adapter.markAsLoaded(next),
        ),
    }),
  },
);

const paginatedAttributeSelector = memoizeSelector((props: MetaScopeOptions) =>
  createSelector(
    selectors.pagination(props),
    selectors.entities(props),
    (pagination, entities) => ({
      meta: pagination,
      rows: entities.map(
        ({
          id,
          name,
          taggingsCount,
          offerTemplateTaggingsCount,
          offerTaggingsCount,
          status,
          searchableName,
        }) => ({
          id,
          count: offerTaggingsCount ?? taggingsCount,
          jobTemplatesCount: offerTemplateTaggingsCount,
          value: name,
          status,
          searchableName,
        }),
      ),
    }),
  ),
);

const tagsSelectors = { ...selectors, paginatedAttributeSelector };

export {
  actions as TagsActions,
  reducer as TagsReducer,
  tagsSelectors,
  namespace as TagsNamespace,
};
